import React, { useEffect, useState } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { ICourseSummary, ITransactionUserSummary } from "@eagerdog/interfaces";
import { type ColDef, GridApi, type TextFilterModel, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./CourseTransactions.module.scss";

interface IProps {
    transactions: ITransactionUserSummary[],
    course: ICourseSummary,
    onRefund(): void,
}

interface IRow {
    time: string,
    processed_amount: string,
    fees: string,
    club_amount: string,
    dog: string,
    refunded: string,
    payment_type: string,
    name: string,
    actions: any
}

const CourseTransactions: React.FC<IProps> = (props) => {
    const [rowData, setRowData] = useState<IRow[]>([]);

    const theme = themeQuartz.withParams({
        fontFamily: "Roboto",
        headerFontFamily: "Roboto",
        cellFontFamily: "Roboto"
    });

    const isSeminar = props.course.course_type === Constants.course_type.seminar;

    let colDefs:ColDef<IRow>[] = [
        { field: "time", headerName: "Registered At", minWidth: 180 },
        { field: "processed_amount", headerName: "Total", minWidth: 120 },
        { field: "fees", headerName: "Fees", minWidth: 100 },
        { field: "club_amount", headerName: "Club Total", minWidth: 130 },
        { field: "dog", headerName: "Dog", minWidth: 150 },
        { field: "refunded", headerName: "Refunded", minWidth: 130 },
        { field: "payment_type", headerName: "Payment Type", minWidth: 150 },
        { field: "name", headerName: "Name", minWidth: 120 },
        {
            headerName: "",
            field: "actions",
            maxWidth: 50,
            minWidth: 50,
            cellRenderer: ActionsRenderer,
            pinned: "right",
            cellRendererParams: {
                onRefund: () => { props.onRefund(); }
            },
            resizable: false,
            cellStyle: {
                "overflow": "visible",
                "zIndex": "2"
            }
        }
    ];

    // Hide dog column for seminars
    if (isSeminar) {
        colDefs = colDefs.filter(col => col.field !== "dog");
    }

    useEffect(() => {
        let rows: IRow[] = [];

        for (let t in props.transactions) {
            let transaction = props.transactions[t];

            let formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: transaction.amounts.currency.toUpperCase(),
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            rows.push({
                time: moment(transaction.created_at).format("MM/DD/YY hh:mm:ssA"),
                processed_amount: formatter.format(((transaction.amounts.processed_total_amount || 0) / 100) || 0),
                fees: formatter.format((((transaction.amounts.processed_processing_fee || 0) + (transaction.amounts.calculated_processing_tax || 0) + (transaction.amounts.processed_application_fee || 0)) / 100) || 0),
                club_amount: formatter.format(((transaction.amounts.processed_amount || 0) / 100)),
                dog: isSeminar 
                    ? "" // No dog for seminars
                    : (transaction.course_registrations[0]?.call_name || "N/A") + " (" + (transaction.course_registrations[0]?.sessions?.length || 0) + " Sessions)",
                refunded: formatter.format((transaction.amounts.refunded_amount || 0) / 100),
                payment_type: transaction.amounts.payment_method_type.charAt(0).toUpperCase() + String(transaction.amounts.payment_method_type).slice(1),
                name: transaction.user.display_name,
                actions: {
                    transaction
                }
            });
        }

        setRowData(rows);
    }, [props.transactions, isSeminar]);

    return (
        <div className={styles.CourseTransactions}>
            <AgGridReact
                rowData={rowData}
                columnDefs={colDefs}
                domLayout="normal"
                gridOptions={{
                    defaultColDef: {
                        flex: 1,
                        minWidth: 100,
                        resizable: true
                    },
                    animateRows: true,
                    rowHeight: 48,
                    headerHeight: 48,
                    overlayNoRowsTemplate: '<span>No transactions available</span>'
                }}
            />
        </div>
    );
};

export default CourseTransactions;
